import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useModal } from '../../components/ModalProvider';
import { Panel, Badge, Button } from '../../components/design';
import ServerKeysSection from './ServerKeysSection';
import ServerAuditSection from './ServerAuditSection';

const CORE_API = '/api';

export default function ConfigTab({ serverId, server, canEdit }) {
  const { alert: showAlert } = useModal();
  const queryClient = useQueryClient();
  const [configEdit, setConfigEdit] = useState(null);
  const [configSaving, setConfigSaving] = useState(false);
  const [syslogSaving, setSyslogSaving] = useState(false);

  const { data: configData } = useQuery({
    queryKey: ['servers', serverId, 'config'],
    queryFn: () => fetch(`${CORE_API}/servers/${serverId}/config`).then(res => res.json()),
  });

  const saveSyslogRouting = async (routing) => {
    setSyslogSaving(true);
    try {
      const res = await fetch(`${CORE_API}/servers/${serverId}/settings/syslog-routing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ routing }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to update');
      await showAlert(`Task queued (${data.task_id}) — rsyslog client config will be written by an SSH worker.`, 'Syslog Routing');
      queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'config'] });
    } catch (err) {
      await showAlert(err.message, 'Error');
    } finally {
      setSyslogSaving(false);
    }
  };

  const saveConfig = async (configType, content) => {
    setConfigSaving(true);
    try {
      if (configType === 'server_settings' || configType === 'exporter_toggle') {
        const res = await fetch(`${CORE_API}/servers/${serverId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(content),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
        queryClient.invalidateQueries({ queryKey: ['servers', serverId, 'config'] });
      } else {
        const res = await fetch(`${CORE_API}/servers/${serverId}/config`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ config_type: configType, content }),
        });
        if (!res.ok) throw new Error((await res.json()).detail || 'Failed to update');
      }
      setConfigEdit(null);
    } catch (err) {
      await showAlert(err.message, 'Error');
    } finally {
      setConfigSaving(false);
    }
  };

  if (!configData) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: '40px 0' }}>
        Loading configuration...
      </div>
    );
  }

  if (configData.error) {
    return (
      <div className="muted" style={{ textAlign: 'center', padding: '40px 0', color: 'var(--danger)' }}>
        Failed to load configuration: {configData.detail || 'Unknown error'}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {configData?.configs?.server && (
        <Panel title="Server Info">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 13 }}>
            {[
              { label: 'Name', value: configData.configs.server.name },
              { label: 'IP Address', value: configData.configs.server.ip_address },
              { label: 'Hostname', value: configData.configs.server.hostname },
              { label: 'OS Type', value: configData.configs.server.os_type },
              { label: 'OS Version', value: configData.configs.server.os_version },
              { label: 'Status', value: configData.configs.server.status },
            ].map(item => (
              <div key={item.label}>
                <div className="muted" style={{ fontSize: 12 }}>{item.label}</div>
                <div style={{ fontWeight: 500, color: 'var(--text-1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.value || '—'}</div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {configData?.configs?.ssh && (
        <Panel title="SSH Connection">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, fontSize: 13 }}>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>User</div>
              <div className="mono" style={{ color: 'var(--text-1)' }}>{configData.configs.ssh.user}</div>
            </div>
            <div>
              <div className="muted" style={{ fontSize: 12 }}>Port</div>
              <div className="mono" style={{ color: 'var(--text-1)' }}>{configData.configs.ssh.port}</div>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <div className="muted" style={{ fontSize: 12 }}>Auth</div>
              <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
                <span style={{ width: 8, height: 8, borderRadius: 9999, background: configData.configs.ssh.has_key ? 'var(--success)' : 'var(--danger)' }} />
                <span style={{ color: 'var(--text-1)' }}>{configData.configs.ssh.has_key ? 'SSH Key Configured' : 'No SSH Key'}</span>
                {configData.configs.ssh.key_name && (
                  <span className="muted" style={{ fontSize: 12 }}>({configData.configs.ssh.key_name})</span>
                )}
              </div>
            </div>
          </div>
        </Panel>
      )}

      <ServerKeysSection serverId={serverId} server={server} canEdit={canEdit} />

      <Panel title="Syslog Routing">
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <select
            value={configData.configs.syslog_routing || 'both'}
            disabled={!canEdit || syslogSaving}
            onChange={(e) => saveSyslogRouting(e.target.value)}
            style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--glass-border)', color: 'var(--text-1)' }}
          >
            <option value="both">Both (514 dual-relay)</option>
            <option value="observability">Observability only (514)</option>
            <option value="security">Security only (5514)</option>
            <option value="none">None</option>
          </select>
          {syslogSaving && <span className="muted" style={{ fontSize: 12 }}>Queuing worker task...</span>}
        </div>
        <div className="muted" style={{ fontSize: 12, marginTop: 8 }}>
          Writes /etc/rsyslog.d/50-synoti-client.conf on the server via an SSH worker task.
          514 = observability, 5514 = Wazuh security.
        </div>
      </Panel>

      <ServerAuditSection serverId={serverId} />

      {configData?.configs?.exporters && configData.configs.exporters.length > 0 && (
        <Panel title="Exporters">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {configData.configs.exporters.map((exporter) => (
              <div key={exporter.name} className="flex items-center justify-between flex-wrap gap-2" style={{ padding: 12, borderRadius: 12, background: 'var(--glass)', border: '1px solid var(--glass-border)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center gap-2">
                    <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--text-1)' }}>{exporter.label}</span>
                    <Badge tone={exporter.enabled ? 'green' : 'gray'}>{exporter.enabled ? 'On' : 'Off'}</Badge>
                    {exporter.active !== null && exporter.active !== undefined && (
                      <Badge tone={exporter.active ? 'green' : 'red'}>{exporter.active ? 'Active' : 'Inactive'}</Badge>
                    )}
                  </div>
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    Port: {exporter.port}
                    {exporter.metrics_url && (
                      <> · <a href={exporter.metrics_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-1)' }}>metrics</a></>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant={exporter.enabled ? 'danger' : 'primary'}
                    size="sm"
                    onClick={() => saveConfig('exporter_toggle', { exporters: [{ name: exporter.name, enabled: !exporter.enabled }] })}
                    disabled={configSaving}
                  >
                    {exporter.enabled ? 'Disable' : 'Enable'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      {configData?.configs?.prometheus_target && (
        <Panel title="Prometheus Target" actions={<Badge tone={configData.configs.prometheus_target.health === 'up' ? 'green' : 'red'}>{configData.configs.prometheus_target.health || 'unknown'}</Badge>}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13 }}>
            <div className="flex between"><span className="muted">Job Name</span><span className="mono" style={{ color: 'var(--text-1)' }}>{configData.configs.prometheus_target.job_name}</span></div>
            <div className="flex between"><span className="muted">Target</span><span className="mono" style={{ color: 'var(--text-1)' }}>{configData.configs.prometheus_target.targets[0]}</span></div>
            <div className="flex between"><span className="muted">Scrape Interval</span><span style={{ color: 'var(--text-1)' }}>{configData.configs.prometheus_target.scrape_interval || '15s'}</span></div>
            {configData.configs.prometheus_target.last_error && (
              <div style={{ padding: '10px 14px', borderRadius: 10, fontSize: 12, background: 'rgba(248,113,113,0.10)', color: 'var(--danger)' }}>
                Last Error: {configData.configs.prometheus_target.last_error}
              </div>
            )}
            <div className="muted" style={{ fontSize: 12 }}>Labels:</div>
            <pre className="mono" style={{ fontSize: 12, color: 'var(--text-2)', background: 'rgba(0,0,0,0.2)', padding: 10, borderRadius: 8, overflowX: 'auto' }}>{JSON.stringify(configData.configs.prometheus_target.labels, null, 2)}</pre>
          </div>
        </Panel>
      )}

      {configData?.configs?.exporters?.find(e => e.name === 'node_exporter') && (
        <Panel title="Node Exporter">
          {configEdit === 'node_exporter_args' ? (
            <div>
              <textarea
                style={{ width: '100%', minHeight: '100px', padding: 10, borderRadius: 8, fontFamily: 'var(--mono)', fontSize: 13, background: 'var(--surface)', border: '1px solid var(--glass-border)', color: 'var(--text-1)' }}
                placeholder="--web.listen-address=:9100 --collector.systemd" id="ne-args-input" />
              <div className="flex items-center gap-2" style={{ marginTop: 12 }}>
                {canEdit && (
                  <Button variant="primary" size="sm" onClick={() => saveConfig('node_exporter_args', document.getElementById('ne-args-input').value)} disabled={configSaving}>
                    {configSaving ? 'Saving...' : 'Save & Restart'}
                  </Button>
                )}
                <Button variant="ghost" size="sm" onClick={() => setConfigEdit(null)}>Cancel</Button>
              </div>
            </div>
          ) : (
            <div>
              {canEdit && (
                <Button variant="ghost" size="sm" onClick={() => setConfigEdit('node_exporter_args')}>Edit Arguments</Button>
              )}
            </div>
          )}
        </Panel>
      )}
    </div>
  );
}
